// ============================================================================
// Persistence: the app's stored/exported shape, and a minimal IndexedDB layer.
//
// What is stored is the MIGRATION ASSET (ROADMAP §1.0): the engine SaveFile
// (append-only action log) plus the clock anchor and last-seen marker. State
// is never stored — it is always rebuilt by replaying the log through the
// driver, which G7 makes bit-identical to the live run. Corollary: a save can
// never be "corrupted mid-life"; the log either replays or it is not a save.
//
// The pure (de)serialization half is DOM-free and unit-tested; only the IDB
// helpers touch the browser.
// ============================================================================

import {
  PARAMS,
  SAVE_FORMAT_VERSION,
  deserialize,
  serialize,
  type SaveFile,
} from '@lifespan/engine';
import { MS_PER_GAME_YEAR, type Clock } from './clock.ts';

export const EXPORT_FORMAT = 'lifespan-export';
export const EXPORT_VERSION = 1;

export interface LastSeen {
  age: number;
  S: number;
}

/** Everything the app persists / exports. */
export interface Persisted {
  format: typeof EXPORT_FORMAT;
  version: number;
  clock: Clock;
  lastSeen: LastSeen | null;
  save: SaveFile;
}

export function packPersisted(save: SaveFile, clock: Clock, lastSeen: LastSeen | null): Persisted {
  return { format: EXPORT_FORMAT, version: EXPORT_VERSION, clock, lastSeen, save };
}

/** Serialize for storage or file export (round-trips doubles exactly). */
export function exportString(p: Persisted): string {
  // The engine's serialize() validates nothing extra here, but routing the
  // SaveFile through it keeps one canonical encoding for both layers.
  return JSON.stringify({ ...p, save: JSON.parse(serialize(p.save)) });
}

/**
 * Parse + validate an exported/stored blob. Throws on anything malformed —
 * loudly and specifically. This is the enforcement point for "the log either
 * replays or it is not a save": anything importString accepts must replay in
 * this app to exactly the state engine.replay() would produce, so every shape
 * the driver cannot faithfully replay (cards-enabled headers, out-of-order or
 * malformed log entries, foreign ParamSets, corrupt clocks) is refused here
 * rather than silently diverging or quietly freezing.
 */
export function importString(str: string): Persisted {
  const obj = JSON.parse(str) as Persisted;
  if (obj.format !== EXPORT_FORMAT) {
    throw new Error(`not a Lifespan export (format=${String(obj.format)})`);
  }
  if (obj.version !== EXPORT_VERSION) {
    throw new Error(
      `unsupported export version ${String(obj.version)} (this build reads v${EXPORT_VERSION})`,
    );
  }
  validateClock(obj.clock);
  if (
    obj.lastSeen != null &&
    (!Number.isFinite(obj.lastSeen.age) || !Number.isFinite(obj.lastSeen.S))
  ) {
    throw new Error('export lastSeen marker is corrupt');
  }
  // Re-validate the embedded SaveFile through the engine's own parser, then
  // enforce the invariants the driver's replay depends on.
  const save = deserialize(JSON.stringify(obj.save));
  validateSave(save);
  return { ...obj, save };
}

function validateClock(c: Clock | undefined): void {
  if (!c || typeof c !== 'object') throw new Error('export missing clock');
  if (typeof c.mode !== 'string' || !(c.mode in MS_PER_GAME_YEAR)) {
    throw new Error(`unknown clock mode ${String((c as { mode?: unknown }).mode)}`);
  }
  if (typeof c.running !== 'boolean') throw new Error('export clock is corrupt (running flag)');
  if (!Number.isFinite(c.anchorAge) || !Number.isFinite(c.anchorWallMs)) {
    throw new Error('export clock is corrupt (non-finite anchor)');
  }
}

function validateSave(save: SaveFile): void {
  const P = PARAMS;
  if (save.formatVersion !== SAVE_FORMAT_VERSION) {
    throw new Error(
      `unsupported save formatVersion ${String(save.formatVersion)} (this build reads v${SAVE_FORMAT_VERSION})`,
    );
  }
  // Replaying a log under a ParamSet other than the one that recorded it
  // produces a life the log never described (GDD §10). v1 has exactly one
  // ParamSet, so mismatch is a hard refusal; a migration path can relax this
  // when a second version exists (ROADMAP M10 save-compatibility promise).
  if (save.paramSetVersion !== P.paramSetVersion) {
    throw new Error(
      `save was recorded under ParamSet v${String(save.paramSetVersion)}; this build runs ` +
        `v${P.paramSetVersion} — replaying it here would produce a different life`,
    );
  }
  // The driver ignores the card header (cards land in M7); accepting a
  // cards-enabled save would silently rebuild a different life than
  // engine.replay. Loud refusal, not silent divergence.
  if (!save.header || save.header.cards !== false) {
    throw new Error('this save uses event cards — unsupported before M7');
  }
  // Validate the invariants that make the driver's drain and engine.replay
  // agree by construction: entries in chronological order at substep-aligned
  // ages; annual reviews dense (year N's review at exactly startAge + N, in
  // order); adjustments only inside a year whose review precedes them.
  let reviewsSeen = 0;
  let prevAge = -Infinity;
  save.log.forEach((entry, i) => {
    if (!entry || !Number.isFinite(entry.atAge)) {
      throw new Error(`save log corrupt at entry ${i}: missing atAge`);
    }
    if (entry.atAge < prevAge - 1e-9) {
      throw new Error(`save log corrupt at entry ${i}: entries out of order`);
    }
    prevAge = entry.atAge;
    // Exact grid check, not a tolerance: the app records every atAge as
    // literally P.startAge + n * P.substep, so requiring bit-equality with the
    // canonical grid value refuses only hand-edited blobs. A tolerance looser
    // than the 1e-9 windows the driver's drain and engine.settleTo use would
    // accept ages the two replay paths place in DIFFERENT substeps — breaking
    // the "anything accepted replays to exactly engine.replay()'s state"
    // contract this function enforces.
    const steps = Math.round((entry.atAge - P.startAge) / P.substep);
    if (steps < 0 || entry.atAge !== P.startAge + steps * P.substep) {
      throw new Error(`save log corrupt at entry ${i}: atAge ${entry.atAge} not substep-aligned`);
    }
    if (entry.kind !== undefined && entry.kind !== 'review' && entry.kind !== 'adjust') {
      throw new Error(`save log corrupt at entry ${i}: unknown kind ${String(entry.kind)}`);
    }
    if (entry.kind === 'adjust') {
      const yr = Math.floor(entry.atAge - P.startAge + 1e-9);
      if (yr >= reviewsSeen) {
        throw new Error(
          `save log corrupt at entry ${i}: adjustment at age ${entry.atAge} precedes its year's review`,
        );
      }
    } else {
      if (entry.atAge !== P.startAge + reviewsSeen) {
        throw new Error(
          `save log corrupt at entry ${i}: expected year-${reviewsSeen} review at atAge ` +
            `${P.startAge + reviewsSeen}, got ${entry.atAge}`,
        );
      }
      reviewsSeen += 1;
    }
    const plan = entry.plan;
    if (
      !plan ||
      P.modeMult[plan.mode] === undefined ||
      !Number.isFinite(plan.livingFrac) ||
      typeof plan.study !== 'boolean'
    ) {
      throw new Error(`save log corrupt at entry ${i}: malformed plan`);
    }
  });
}

// ---------------------------------------------------------------------------
// IndexedDB (browser only) — one object store, one key. No dependencies.
// ---------------------------------------------------------------------------

const DB_NAME = 'lifespan';
const DB_VERSION = 1;
const STORE = 'kv';
const KEY = 'session-v1';
/** One-step undo slot: the previous blob survives one import/wipe/bad-load. */
const KEY_PREV = 'session-v1-prev';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

export async function loadPersisted(): Promise<Persisted | null> {
  const db = await openDb();
  try {
    const str = await new Promise<string | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result as string | undefined);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB read failed'));
    });
    return str ? importString(str) : null;
  } finally {
    db.close();
  }
}

export async function storePersisted(p: Persisted): Promise<void> {
  // Serialize SYNCHRONOUSLY at call time: the caller's session/clock are live
  // mutable references, and a settle tick between this call and the IDB put
  // must not tear the snapshot (a blob newer than its own lastSeen marker).
  const blob = exportString(p);
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB write failed'));
    });
  } finally {
    db.close();
  }
}

/** Copy the current blob into the backup slot (before import/wipe/bad-load). */
export async function backupPersisted(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const req = store.get(KEY);
      req.onsuccess = () => {
        if (req.result !== undefined) store.put(req.result, KEY_PREV);
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB backup failed'));
    });
  } finally {
    db.close();
  }
}

/** The raw backup blob, unvalidated (recovery download — never discard data). */
export async function loadBackupRaw(): Promise<string | null> {
  const db = await openDb();
  try {
    const str = await new Promise<string | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY_PREV);
      req.onsuccess = () => resolve(req.result as string | undefined);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB read failed'));
    });
    return str ?? null;
  } finally {
    db.close();
  }
}

export async function wipePersisted(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB delete failed'));
    });
  } finally {
    db.close();
  }
}
